import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { StatusChip } from "components/StatusChip";

import { DiscrepancyTallyView } from "./sections/discrepancyBreakdown";

export interface DiscrepanciesCellProps {
  /** Open-discrepancy counts split by kind (compliance vs field). */
  tally: DiscrepancyTallyView;
  /** When provided, clicking the cell opens the detail modal (and stops the grid row click). */
  onClick?: () => void;
}

/** Tooltip body: a bold header plus a per-line breakdown. */
function BubbleBreakdown({ title, lines }: { title: string; lines: [string, number][] }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.25 }}>
        {title}
      </Typography>
      {lines.map(([label, value]) => (
        <Typography key={label} variant="caption" component="div">
          {label}: {value}
        </Typography>
      ))}
    </Box>
  );
}

/**
 * Two discrepancy "bubbles": compliance issues (missing e-Manifest + EPA
 * correction requests) and field discrepancies (cross-source value
 * disagreements). Each chip shows its open total; hovering breaks it down by
 * type (compliance) or status (field). A bubble is omitted when its total is
 * zero, and the cell renders nothing when the manifest/line is clean. Shared by
 * the manifest list view and the line-item table.
 */
export function DiscrepanciesCell({ tally, onClick }: DiscrepanciesCellProps) {
  const {
    complianceTotal,
    missingEManifest,
    epaCorrection,
    fieldTotal,
    fieldUnresolved,
    fieldPending,
  } = tally;

  if (complianceTotal === 0 && fieldTotal === 0) {
    return null;
  }

  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      onClick={
        onClick
          ? (event) => {
              event.stopPropagation();
              onClick();
            }
          : undefined
      }
      sx={onClick ? { cursor: "pointer" } : undefined}
    >
      {complianceTotal > 0 && (
        <Tooltip
          placement="top"
          enterDelay={400}
          title={
            <BubbleBreakdown
              title="EPA-flagged"
              lines={[
                ["Missing e-Manifest", missingEManifest],
                ["EPA correction requests", epaCorrection],
                ["Total", complianceTotal],
              ]}
            />
          }
        >
          <StatusChip label={complianceTotal} color="error" />
        </Tooltip>
      )}
      {fieldTotal > 0 && (
        <Tooltip
          placement="top"
          enterDelay={400}
          title={
            <BubbleBreakdown
              title="Encamp-flagged"
              lines={[
                ["Unresolved", fieldUnresolved],
                ["Pending", fieldPending],
                ["Total", fieldTotal],
              ]}
            />
          }
        >
          <StatusChip label={fieldTotal} color="success" />
        </Tooltip>
      )}
    </Stack>
  );
}
